import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AuthenticatedTemplate, UnauthenticatedTemplate, useMsal } from '@azure/msal-react';
import type { ModuleSidebar } from '@one/module-sdk';
import {
  applyTheme,
  Avatar,
  brandColorFor,
  Button,
  Card,
  Dot,
  Icon,
  IconButton,
  Kbd,
  Notice,
  PageHead,
  Segmented,
  StatusBadge,
  resolveTheme,
  type ThemeName,
  type ThemePreference,
} from '@one/ui';
import { authConfigured, loginRequest, msalInstance } from './auth';
import { Befehlspalette, befehlspaletteOeffnen, type Befehl } from './Befehlspalette';
import { API_BASE, ApiAbgelehnt, apiFetch, type ShellServices } from './modules/context';
import { ModuleHost, type Sprungziel } from './modules/ModuleHost';
import { Rueckfragedialog } from './modules/Rueckfragedialog';
import { erzeugeRueckfragen } from './modules/rueckfrage';
import { oeffenbar, statusText } from './modules/status';
import { vorschauVon } from './modules/vorschau';
import {
  INFO_PRAEFIX,
  ModulInfo,
  infoGruppe,
  infoSeiteAus,
  infoTitel,
  useModulStandards,
  type InfoSeite,
} from './modules/ModulInfo';
import type { Bootstrap, ManifestEntry, TenantInfo } from './modules/types';
import { erzeugeAufwaermer, type ApiStatus } from './aufwaermen';
import { adresseFuer, adresseLesen, adressSchritt } from './adresse';

/**
 * Modul, Seite und Shell-Seite aus der Adresse des Browsers. Nur freigegebene Module
 * (`bootstrap.modules`) — eine Adresse auf ein fremdes Modul landet auf der Startseite, ebenso
 * eine auf ein inaktives Modul (Kap. 08 §1.3).
 */
function startAusAdresse(alle: readonly ManifestEntry[]): {
  modul: ManifestEntry | null;
  ziel: Sprungziel | undefined;
  info: InfoSeite | null;
} {
  const module = alle.filter(nutzbar);
  const a = adresseLesen(window.location.pathname, window.location.hash, module);
  const modul = a ? (module.find((m) => m.id === a.modulId) ?? null) : null;
  if (!modul) return { modul: null, ziel: undefined, info: null };
  const info = infoSeiteAus(a?.pfad);
  if (info) return { modul, ziel: undefined, info };
  return { modul, ziel: a?.pfad ? { pfad: a.pfad, nr: 0 } : undefined, info: null };
}

/**
 * Holt die Startdaten und hält dabei einen Kaltstart der API aus: in der
 * dev-Umgebung läuft die Container App auf 0 Replicas, der erste Aufruf nach einer
 * Ruhephase kann deshalb einmal ins Leere gehen. Ohne Wiederholung sähe die erste
 * Anmeldung des Tages eine Fehlermeldung — mit Wiederholung nur eine Sekunde länger.
 */
const VERSUCHE = 4;

function useBootstrap(mandant?: string) {
  const [data, setData] = useState<Bootstrap | null>(null);
  // Für welchen Wunsch-Mandanten `data` geladen wurde — bis der neue Bootstrap da ist,
  // gehören die Daten noch zum vorherigen.
  const [geladenFuer, setGeladenFuer] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [startet, setStartet] = useState(false);

  useEffect(() => {
    let abgebrochen = false;

    (async () => {
      for (let versuch = 1; versuch <= VERSUCHE; versuch++) {
        try {
          const response = await apiFetch('core/bootstrap', undefined, mandant);
          if (!response.ok) throw new Error(`API antwortete mit HTTP ${response.status}`);
          const geladen = (await response.json()) as Bootstrap;
          if (!abgebrochen) {
            setData(geladen);
            setGeladenFuer(mandant);
          }
          return;
        } catch (err: unknown) {
          if (abgebrochen) return;
          // Eine Ablehnung durch die API wiederholen zu wollen ist sinnlos —
          // im Unterschied zu einem Kaltstart oder einem Netzwerkfehler.
          if (err instanceof ApiAbgelehnt || versuch === VERSUCHE) {
            setError(err instanceof Error ? err.message : String(err));
            return;
          }
          setStartet(true);
          await new Promise((r) => setTimeout(r, 800 * versuch));
        }
      }
    })();

    return () => {
      abgebrochen = true;
    };
    // Beim Mandantenwechsel neu laden: Rechte und Module gelten je Mandant, und
    // sie im Browser weiterzuverwenden hieße, eine fremde Sicht zu behalten.
  }, [mandant]);

  return { data, geladenFuer, error, startet };
}

/**
 * Kennzahlen für die Kacheln. Konvention: `GET /api/<modul>/stats` liefert
 * `{ stats: [{ value, label }] }`. Ein Modul ohne diesen Endpoint bekommt eine
 * Kachel ohne Zahlen — das ist kein Fehler und wird deshalb still verschluckt.
 */
interface Kennzahl {
  value: string | number;
  label: string;
}

function useKennzahlen(module: ManifestEntry[], mandant: string | undefined) {
  const [werte, setWerte] = useState<Record<string, Kennzahl[]>>({});

  useEffect(() => {
    let abgebrochen = false;
    setWerte({});
    void Promise.all(
      module.map(async (m) => {
        try {
          const antwort = await apiFetch(`${m.id}/stats`, undefined, mandant);
          if (!antwort.ok) return;
          const daten = (await antwort.json()) as { stats?: Kennzahl[] };
          if (!abgebrochen && daten.stats?.length) {
            // Zwei Kennzahlen je Kachel (Designsystem, Startseite).
            setWerte((vorher) => ({ ...vorher, [m.id]: daten.stats!.slice(0, 2) }));
          }
        } catch {
          /* Eine Kachel ohne Zahlen ist in Ordnung. */
        }
      }),
    );
    return () => {
      abgebrochen = true;
    };
  }, [module, mandant]);

  return werte;
}

/**
 * Hinweise der Module für die Startseite (Prototyp: „Dir fehlen 5 Projektzeiten
 * von gestern" mit Knopf daneben).
 *
 * Das ist der Unterschied zwischen einem Menü und einem Arbeitsplatz: Eine
 * Startseite, die nur Kacheln zeigt, verlangt von jedem, sich selbst zu erinnern,
 * was offen ist. Eine, die es sagt, nimmt die Erinnerung ab.
 *
 * Konvention wie bei den Kennzahlen: `GET /api/<modul>/hinweise` →
 * `{ hinweise: [{ id, text, aktion?: { text, ziel } }] }`. Jedes Modul weiß
 * selbst, was bei ihm offen ist, und prüft dabei seine eigenen Rechte. Ein Modul
 * ohne diesen Endpoint trägt nichts bei, und das ist kein Fehler.
 *
 * `ziel` ist ein Unterpfad **innerhalb** des Moduls (z. B. `heute`).
 */
interface Hinweis {
  id: string;
  text: string;
  aktion?: { text: string; ziel: string };
  dringlichkeit?: 'info' | 'wichtig';
}
interface HinweisMitModul extends Hinweis {
  modul: ManifestEntry;
}

/** Mehr als drei Zeilen liest niemand, und sie verdrängen die Kacheln. */
const HINWEISE_MAX = 3;

function useHinweise(module: ManifestEntry[], mandant: string | undefined) {
  const [hinweise, setHinweise] = useState<HinweisMitModul[]>([]);

  useEffect(() => {
    let abgebrochen = false;
    setHinweise([]);
    void Promise.all(
      module.map(async (m) => {
        try {
          const antwort = await apiFetch(`${m.id}/hinweise`, undefined, mandant);
          if (!antwort.ok) return [];
          const daten = (await antwort.json()) as { hinweise?: Hinweis[] };
          return (daten.hinweise ?? []).map((h) => ({ ...h, modul: m }));
        } catch {
          return [];
        }
      }),
    ).then((teile) => {
      if (abgebrochen) return;
      // Wichtiges zuerst, sonst in der Reihenfolge der Module — die ist die des
      // Arbeitstags (siehe GRUPPEN_REIHENFOLGE) und damit eine sinnvolle Ordnung.
      const alle = teile.flat();
      alle.sort(
        (a, b) => Number(b.dringlichkeit === 'wichtig') - Number(a.dringlichkeit === 'wichtig'),
      );
      setHinweise(alle.slice(0, HINWEISE_MAX));
    });
    return () => {
      abgebrochen = true;
    };
  }, [module, mandant]);

  return hinweise;
}

interface AnwesendePerson {
  id: string;
  name: string;
  initialen: string;
  upn: string | null;
  status: 'da' | 'urlaub' | 'berufsschule' | 'abwesend';
}
interface Anwesenheit {
  personen: AnwesendePerson[];
  weitere: number;
  abwesenheitenBekannt: boolean;
}

const STATUS_TEXT: Record<AnwesendePerson['status'], string> = {
  da: 'da',
  urlaub: 'Urlaub',
  berufsschule: 'Berufsschule',
  abwesend: 'abwesend',
};
const STATUS_TON = {
  da: 'good',
  urlaub: 'warn',
  berufsschule: 'info',
  abwesend: 'muted',
} as const;

/**
 * „Team heute" im Fuß der Sidebar.
 *
 * Ein Klick öffnet den Teams-Chat mit der Person. Das ist der eigentliche Zweck:
 * nicht zu wissen, wer da ist, sondern die Person zu erreichen, die da ist —
 * ohne den Umweg über die Suche in einem anderen Programm.
 *
 * Der Deeplink `teams.microsoft.com/l/chat/0/0?users=<upn>` öffnet die
 * Teams-App, wenn sie installiert ist, sonst Teams im Browser. Ohne UPN (eine
 * Person aus Altdaten ohne Entra-Konto) gibt es keinen Link — dann steht der
 * Avatar ohne Verweis da, statt einen zu versprechen, der ins Leere führt.
 */
function TeamHeute({ mandant }: { mandant: string | undefined }) {
  const [daten, setDaten] = useState<Anwesenheit | null>(null);

  useEffect(() => {
    let abgebrochen = false;
    setDaten(null);
    void (async () => {
      try {
        const antwort = await apiFetch('core/anwesenheit', undefined, mandant);
        if (!antwort.ok) return;
        const geladen = (await antwort.json()) as Anwesenheit;
        if (!abgebrochen) setDaten(geladen);
      } catch {
        /* Ein Streifen, der fehlt, ist besser als eine Fehlermeldung am Rand. */
      }
    })();
    return () => {
      abgebrochen = true;
    };
  }, [mandant]);

  if (!daten || daten.personen.length === 0) return null;

  return (
    <div className="one-sidebar-foot">
      <span className="one-overline">
        Team heute{daten.abwesenheitenBekannt ? '' : ' · ohne Abwesenheiten'}
      </span>
      <div className="one-team">
        {daten.personen.map((p) => {
          const titel = `${p.name}, ${STATUS_TEXT[p.status]}${p.upn ? ' · in Teams schreiben' : ''}`;
          const inhalt = (
            <>
              {p.initialen.slice(0, 2)}
              <Dot tone={STATUS_TON[p.status]} aria-hidden />
            </>
          );
          return p.upn ? (
            <a
              key={p.id}
              className="one-avatar"
              data-away={p.status === 'da' ? undefined : 'true'}
              aria-label={titel}
              title={titel}
              href={`https://teams.microsoft.com/l/chat/0/0?users=${encodeURIComponent(p.upn)}`}
              target="_blank"
              rel="noreferrer"
            >
              {inhalt}
            </a>
          ) : (
            <span
              key={p.id}
              className="one-avatar"
              role="img"
              data-away={p.status === 'da' ? undefined : 'true'}
              aria-label={titel}
              title={titel}
            >
              {inhalt}
            </span>
          );
        })}
        {daten.weitere > 0 && (
          <span className="one-avatar" data-away="true" aria-label={`${daten.weitere} weitere`}>
            +{daten.weitere}
          </span>
        )}
      </div>
      <div className="one-team-legend">
        <span>
          <Dot tone="good" /> da
        </span>
        <span>
          <Dot tone="warn" /> Urlaub
        </span>
        <span>
          <Dot tone="muted" /> abwesend
        </span>
      </div>
    </div>
  );
}

interface ToastEintrag {
  id: number;
  text: string;
  kind: string;
}

const TOAST_ICON: Record<string, string> = {
  success: 'circle-check',
  error: 'circle-alert',
  info: 'info',
};

function Toasts({ items }: { items: ToastEintrag[] }) {
  if (items.length === 0) return null;
  return (
    <div className="one-toast-region" role="status" aria-live="polite">
      {items.map((t) => (
        <div
          key={t.id}
          className="one-toast"
          data-tone={t.kind === 'error' ? 'danger' : t.kind === 'success' ? 'good' : undefined}
        >
          <Icon name={TOAST_ICON[t.kind] ?? 'info'} />
          {t.text}
        </div>
      ))}
    </div>
  );
}

/**
 * Der Zustand nach dem allerersten Login (Konzept Kap. 03 §4): Die Person ist in
 * ONE bekannt, hat aber noch keine Mitgliedschaft — und damit bewusst keinerlei
 * Sichtbarkeit. Ihr hier ehrlich zu sagen, worauf sie wartet, ist der Unterschied
 * zwischen „ONE ist kaputt" und „ONE wartet auf einen Klick".
 */
function Wartekarte({ user }: { user: Bootstrap['user'] }) {
  const ausgeschieden = user.status === 'left';
  return (
    <Card>
      <h1 className="one-page-title">
        {ausgeschieden ? 'Dieser Zugang ist geschlossen.' : 'Fast geschafft.'}
      </h1>
      <p className="one-prose">
        {ausgeschieden ? (
          <>
            Das Konto <strong>{user.email}</strong> ist in ONE nicht mehr aktiv. Wenn das ein Irrtum
            ist, hilft die Administration deines Mandanten weiter.
          </>
        ) : (
          <>
            Deine Anmeldung hat geklappt, <strong>{user.displayName}</strong> — dein Zugang wartet
            nur noch auf die Freischaltung durch die Administration deines Mandanten. Danach
            erscheinen hier deine Module. Ein Neuladen der Seite genügt.
          </>
        )}
      </p>
    </Card>
  );
}

/**
 * Die Leiste der Rollen-Simulation (Kap. 04 §3): dauerhaft sichtbar, nicht
 * wegklickbar. Wer vergisst, dass er eine fremde Sicht ansieht, meldet Fehler,
 * die keine sind — und trifft Entscheidungen auf falscher Grundlage.
 */
function SimulationsLeiste({
  simulation,
  beenden,
}: {
  simulation: NonNullable<Bootstrap['simulation']>;
  beenden: () => void;
}) {
  return (
    <div className="one-simulation" role="status">
      <span className="one-badge">Simulation</span>
      <span>
        Du siehst ONE als <strong>{simulation.roleKey}</strong> bei{' '}
        <strong>{simulation.companyName}</strong> — rein lesend, protokolliert.
      </span>
      <span className="one-topbar-spacer" />
      <Button variant="secondary" size="sm" onClick={beenden}>
        Simulation beenden
      </Button>
    </div>
  );
}

/**
 * Bildmarken der Mandanten (Designsystem, assets/Mandanten). Sie erscheinen nur
 * im Rail-Button und im Mandantenwechsel — nie als Seitenkopf, nie umgefärbt.
 */
const MANDANTEN_LOGO: Record<string, { hell: string; dunkel?: string }> = {
  verch: {
    hell: '/mandanten/verch-logo-mark-blue.png',
    dunkel: '/mandanten/verch-logo-mark-white.png',
  },
  tt: { hell: '/mandanten/tonitec-bildmarke-red.png' },
  feps: { hell: '/mandanten/feps-logo-teal.png', dunkel: '/mandanten/feps-logo-white.png' },
  umd: { hell: '/mandanten/umd-logo-square.png' },
  wg: {
    hell: '/mandanten/wildegrillerei-logo-light.jpg',
    dunkel: '/mandanten/wildegrillerei-logo-dark.jpg',
  },
  qplus: { hell: '/mandanten/qliving-logo-250.png' },
};

function logoFuer(t: TenantInfo, theme: ThemeName): string | undefined {
  if (t.logo) return t.logo;
  const l = MANDANTEN_LOGO[t.slug];
  return l ? (theme === 'dark' ? (l.dunkel ?? l.hell) : l.hell) : undefined;
}

/**
 * Module nach Gruppe, Gruppen in einer festen Reihenfolge.
 *
 * Die Reihenfolge ist nicht alphabetisch, sondern die des Arbeitstags: Womit
 * jemand täglich arbeitet, steht oben; Verwaltung und Werkzeuge unten. Eine
 * Gruppe, die hier fehlt, verschwindet nicht: Sie wird hinten angehängt. Ein
 * neues Modul soll ohne Änderung an der Shell erscheinen können.
 */
const GRUPPEN_REIHENFOLGE = [
  'Arbeit & Steuerung',
  'Marketing & Kreativ',
  'Einkauf',
  'Kundenservice',
  'E-Commerce',
  'Finanzen & Verwaltung',
  'Organisation & IT',
];
const OHNE_GRUPPE = 'Weitere';

function nachGruppen(module: ManifestEntry[]): [string, ManifestEntry[]][] {
  const gruppen = new Map<string, ManifestEntry[]>();
  for (const m of module) {
    const name = m.category?.trim() || OHNE_GRUPPE;
    (gruppen.get(name) ?? gruppen.set(name, []).get(name)!).push(m);
  }
  const rang = (name: string) => {
    const i = GRUPPEN_REIHENFOLGE.indexOf(name);
    // Unbekannte Gruppe hinten, die Sammelgruppe ganz zuletzt.
    return name === OHNE_GRUPPE ? 999 : i === -1 ? 500 : i;
  };
  return [...gruppen.entries()].sort((a, b) => rang(a[0]) - rang(b[0]));
}

/** „Guten Morgen" um 8 und „Guten Abend" um 20 Uhr — ein Gruß, der die
 *  Tageszeit ignoriert, klingt wie eine Maschine. */
function gruss(stunde: number): string {
  if (stunde < 11) return 'Guten Morgen';
  if (stunde < 18) return 'Guten Tag';
  return 'Guten Abend';
}

const modulIcon = (m: ManifestEntry) => m.icon || 'layout-grid';

/**
 * Ob das Modul in der Shell geöffnet werden kann (Kap. 08 §1.3): ein inaktives nicht — es steht
 * sichtbar im Menü, ist aber nicht klickbar. Tester mit laufender Modulvorschau öffnen es doch.
 */
function nutzbar(m: ManifestEntry): boolean {
  return oeffenbar(m.status, vorschauVon(m) !== null);
}

/** Name mit Status für Tooltip und Screenreader: „Arbeitszeit · inaktiv“. */
function mitStatus(m: ManifestEntry): string {
  const text = statusText(m.status);
  return text ? `${m.title} · ${text.toLowerCase()}` : m.title;
}

/** Statusbadge BETA oder INAKTIV; ein produktives Modul trägt keines. */
function Phase({ status }: { status?: string }) {
  const text = statusText(status);
  if (!text) return null;
  return <StatusBadge kind={text === 'Beta' ? 'beta' : 'inaktiv'}>{text}</StatusBadge>;
}

function Startseite({
  bootstrap,
  kennzahlen,
  hinweise,
  oeffnen,
}: {
  bootstrap: Bootstrap;
  kennzahlen: Record<string, Kennzahl[]>;
  hinweise: HinweisMitModul[];
  oeffnen: (entry: ManifestEntry, ziel?: string) => void;
}) {
  const vorname = bootstrap.user.displayName.split(' ')[0];
  const mandant = bootstrap.tenants.find((t) => t.id === bootstrap.activeTenantId)?.name;

  if (bootstrap.modules.length === 0) {
    return (
      <>
        <PageHead title={`${gruss(new Date().getHours())}, ${vorname}.`} />
        <div className="one-empty">
          <Icon name="layout-grid" />
          <div className="one-empty-title">Die Plattform steht — die Module kommen.</div>
          <div>
            In {mandant} ist noch kein Modul freigeschaltet. Die Modul-Matrix in der Verwaltung
            entscheidet darüber; sobald dort etwas aktiv ist, erscheint es hier.
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHead
        title={`${gruss(new Date().getHours())}, ${vorname}.`}
        sub={
          <>
            Deine Module in {mandant}. Mit <Kbd>Strg</Kbd> <Kbd>K</Kbd> springst du überall hin.
          </>
        }
      />
      {/* Steht über den Kacheln: Was offen ist, ist wichtiger als die Auswahl,
          wohin man gehen könnte. */}
      {hinweise.length > 0 && (
        <div className="shell-hinweise">
          {hinweise.map((h) => (
            <Notice
              key={`${h.modul.id}:${h.id}`}
              tone={h.dringlichkeit === 'wichtig' ? 'warn' : 'accent'}
              title={h.modul.title}
              action={
                h.aktion && (
                  <Button size="sm" onClick={() => oeffnen(h.modul, h.aktion!.ziel)}>
                    {h.aktion.text}
                  </Button>
                )
              }
            >
              {h.text}
            </Notice>
          ))}
        </div>
      )}
      <div className="shell-gruppen">
        {nachGruppen(bootstrap.modules).map(([gruppe, module]) => (
          <section key={gruppe} className="one-section">
            <h2 className="one-section-title">{gruppe}</h2>
            <div className="one-grid">
              {module.map((entry) => (
                <button
                  key={entry.id}
                  type="button"
                  className="one-card shell-kachel"
                  data-inaktiv={nutzbar(entry) ? undefined : 'true'}
                  aria-disabled={nutzbar(entry) ? undefined : true}
                  title={nutzbar(entry) ? undefined : mitStatus(entry)}
                  onClick={() => nutzbar(entry) && oeffnen(entry)}
                >
                  <span className="shell-kachel-kopf">
                    <Icon name={modulIcon(entry)} knoten={entry.iconKnoten} />
                    <span className="one-card-title">{entry.title}</span>
                    <Phase status={entry.status} />
                  </span>
                  {entry.description && (
                    <span className="shell-kachel-text">{entry.description}</span>
                  )}
                  {kennzahlen[entry.id] && (
                    <span className="shell-kachel-zahlen">
                      {kennzahlen[entry.id]!.map((k) => (
                        <span key={k.label} className="shell-kachel-zahl">
                          <strong>{k.value}</strong>
                          <span>{k.label}</span>
                        </span>
                      ))}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </section>
        ))}
      </div>
    </>
  );
}

/** Klick daneben oder Esc schließt ein Aufklappmenü. */
function useSchliessen(offen: boolean, schliessen: () => void) {
  useEffect(() => {
    if (!offen) return;
    const klick = () => schliessen();
    const taste = (e: KeyboardEvent) => e.key === 'Escape' && schliessen();
    window.addEventListener('click', klick);
    window.addEventListener('keydown', taste);
    return () => {
      window.removeEventListener('click', klick);
      window.removeEventListener('keydown', taste);
    };
  }, [offen, schliessen]);
}

/**
 * Mandantenwechsel als Menü (Designsystem „MandantSwitcher"). Der Wechsel geht
 * über die API zurück: Rechte und Module gelten je Mandant und werden neu geholt,
 * nicht im Browser umgeschaltet — erst danach wechseln die Farben.
 */
function MandantenMenue({
  tenants,
  aktiv,
  theme,
  wechseln,
}: {
  tenants: TenantInfo[];
  aktiv: TenantInfo;
  theme: ThemeName;
  wechseln: (slug: string) => void;
}) {
  return (
    <div className="one-menu" role="listbox" aria-label="Mandant wechseln">
      <div className="one-menu-head">
        <span className="one-overline">Mandant wechseln</span>
      </div>
      {tenants.map((t) => {
        const logo = logoFuer(t, theme);
        return (
          <button
            key={t.id}
            type="button"
            role="option"
            aria-selected={t.id === aktiv.id}
            className="one-menu-item"
            data-active={t.id === aktiv.id ? 'true' : undefined}
            onClick={() => wechseln(t.slug)}
          >
            {logo ? (
              <img src={logo} alt="" />
            ) : (
              <span className="sw" style={{ background: brandColorFor(t.slug, t.color) }} />
            )}
            {t.name}
            {t.id === aktiv.id && <Icon name="check" size="sm" />}
          </button>
        );
      })}
    </div>
  );
}

function initialen(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((t) => t[0]?.toUpperCase() ?? '')
    .join('');
}

const THEME_KEY = 'one.theme';
const SIDEBAR_KEY = 'one.sidebar';
// Zuletzt gewählter Mandant: Ohne ihn startete ein Neuladen (F5) beim ersten erlaubten
// Mandanten, und die Adresse eines dort nicht freigegebenen Moduls endete auf der
// Startseite. Ein nicht (mehr) erlaubter Wert ist harmlos: Die API fällt dann selbst
// auf den ersten erlaubten Mandanten zurück.
const MANDANT_KEY = 'one.mandant';

/**
 * Schmale Bildschirme (Telefon, B3): Die Sidebar verdeckt dort den Inhalt. Sie startet
 * deshalb eingeklappt und klappt nach der Wahl einer Seite wieder zu; die gespeicherte
 * Wahl gilt nur für breite Bildschirme.
 * Achtung: Die 760px sind mit dem `@media`-Block in styles.css gekoppelt; beide
 * Stellen gemeinsam ändern.
 */
const SCHMAL = '(max-width: 760px)';
function istSchmal(): boolean {
  try {
    return window.matchMedia(SCHMAL).matches;
  } catch {
    return false;
  }
}

function gespeichert(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}
function speichern(key: string, wert: string) {
  try {
    window.localStorage.setItem(key, wert);
  } catch {
    /* Ohne Speicher gilt die Wahl nur für diese Sitzung. */
  }
}

/**
 * Theme-Wahl System / Hell / Dunkel (Profilmenü). Gespeichert wird im Browser
 * der Person; „System" folgt der Einstellung des Betriebssystems auch live.
 */
function useTheme() {
  const [wahl, setWahl] = useState<ThemePreference>(
    () => (gespeichert(THEME_KEY) as ThemePreference | null) ?? 'system',
  );
  const [system, setSystem] = useState<ThemeName>(() => resolveTheme('system'));

  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const neu = () => setSystem(mq.matches ? 'dark' : 'light');
    mq.addEventListener('change', neu);
    return () => mq.removeEventListener('change', neu);
  }, []);

  const theme: ThemeName = wahl === 'system' ? system : wahl;
  const waehlen = useCallback((w: ThemePreference) => {
    setWahl(w);
    speichern(THEME_KEY, w);
  }, []);
  return { wahl, theme, waehlen };
}

function Platform({
  bootstrap,
  mandantWechseln,
}: {
  bootstrap: Bootstrap;
  mandantWechseln: (slug: string) => void;
}) {
  const tenant: TenantInfo =
    bootstrap.tenants.find((t) => t.id === bootstrap.activeTenantId) ?? bootstrap.tenants[0]!;
  // Beim Laden gilt die Adresse: Neuladen und Deep-Links öffnen Modul und Seite wieder (B4, PF-G).
  const [start] = useState(() => startAusAdresse(bootstrap.modules));
  const [openModule, setOpenModule] = useState<ManifestEntry | null>(start.modul);
  const [ziel, setZiel] = useState<Sprungziel | undefined>(start.ziel);
  // Ein gesetztes Ziel (Adresse, Klick, Palette, Zurück) gilt vor `sidebar.active`, bis das
  // offene Modul eine neue Sidebar meldet — sonst schriebe die alte Seite die Adresse zurück.
  const [zielVorrang, setZielVorrang] = useState(true);
  // Zählt „Zurück"/„Vor", damit der Adress-Effekt danach sicher einmal läuft (und
  // `erwartet` auflöst), auch wenn sich die Adresse dabei nicht ändert.
  const [verlaufNr, setVerlaufNr] = useState(0);
  // Shell-eigene Modulseite (ADR-008: Plan, Doku, Daten, Protokoll) — null = das Modul selbst.
  const [infoSeite, setInfoSeite] = useState<InfoSeite | null>(start.info);
  const [toasts, setToasts] = useState<ToastEintrag[]>([]);
  // Warteschlange für ctx.ui.confirm; eine Instanz je Shell-Sitzung.
  const [rueckfragen] = useState(erzeugeRueckfragen);
  useEffect(() => () => rueckfragen.alleAbbrechen(), [rueckfragen]);
  const [sidebars, setSidebars] = useState<Record<string, ModuleSidebar>>({});
  const [badges, setBadges] = useState<Record<string, number>>({});
  const [menue, setMenue] = useState<'mandant' | 'mandant-crumb' | 'profil' | null>(null);
  const [eingeklappt, setEingeklappt] = useState(
    () => istSchmal() || gespeichert(SIDEBAR_KEY) === 'zu',
  );
  // Beim Wechsel der Breite (Drehen, Fenstergröße) mitziehen: schmal klappt ein,
  // breit stellt die gespeicherte Wahl wieder her.
  useEffect(() => {
    let mq: MediaQueryList;
    try {
      mq = window.matchMedia(SCHMAL);
    } catch {
      return;
    }
    const bei = (e: MediaQueryListEvent) =>
      setEingeklappt(e.matches || gespeichert(SIDEBAR_KEY) === 'zu');
    mq.addEventListener('change', bei);
    return () => mq.removeEventListener('change', bei);
  }, []);
  // Inaktive Module (Kap. 08 §1.3) stehen im Menü, öffnen sich aber nicht und tragen weder
  // Kennzahlen noch Hinweise bei.
  const nutzbare = useMemo(() => bootstrap.modules.filter(nutzbar), [bootstrap.modules]);
  const kennzahlen = useKennzahlen(nutzbare, tenant.slug);
  const hinweise = useHinweise(nutzbare, tenant.slug);
  const { wahl, theme, waehlen } = useTheme();
  const schliessen = useCallback(() => setMenue(null), []);
  useSchliessen(menue !== null, schliessen);

  // Theme und Mandanten-Akzent auf <html>: erst nachdem Bootstrap für den neuen
  // Mandanten geladen ist — so blitzt nie das alte Modul in neuen Farben auf.
  const themeRef = useRef(theme);
  const themeListener = useRef(new Set<(t: ThemeName) => void>());
  useEffect(() => {
    applyTheme(document.documentElement, {
      theme,
      tenantSlug: tenant.slug,
      tenantColor: tenant.color,
    });
    if (themeRef.current !== theme) {
      themeRef.current = theme;
      themeListener.current.forEach((cb) => cb(theme));
    }
  }, [theme, tenant.slug, tenant.color]);

  const sprungNr = useRef(0);
  // Nach „Zurück"/„Vor" erwartete Adresse (siehe `adressSchritt`); jede eigene
  // Navigation beendet das Warten darauf.
  const erwartet = useRef<string | null>(null);
  // Die Sidebar eines anderen Moduls ist veraltet (alte `active`) — bis das Modul
  // sich neu meldet, gilt sie nicht.
  function sidebarVerwerfen(id: string) {
    setSidebars((vorher) => {
      if (!(id in vorher)) return vorher;
      const rest = { ...vorher };
      delete rest[id];
      return rest;
    });
  }
  function modulOeffnen(entry: ManifestEntry, pfad?: string) {
    erwartet.current = null;
    if (offenRef.current?.id !== entry.id) sidebarVerwerfen(entry.id);
    setInfoSeite(null);
    setZiel(pfad ? { pfad, nr: ++sprungNr.current } : undefined);
    setZielVorrang(true);
    setOpenModule(entry);
  }
  function seiteOeffnen(pfad: string) {
    erwartet.current = null;
    const info = infoSeiteAus(pfad);
    if (info) {
      setInfoSeite(info);
      return;
    }
    setInfoSeite(null);
    setZiel({ pfad, nr: ++sprungNr.current });
    setZielVorrang(true);
  }
  function startseite() {
    erwartet.current = null;
    setOpenModule(null);
    setZiel(undefined);
    setInfoSeite(null);
  }

  const toast: ShellServices['toast'] = (text, kind = 'info') => {
    const id = Date.now() + Math.floor(performance.now());
    setToasts((prev) => [...prev, { id, text, kind }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 2800);
  };

  // Abmelden beendet die Sitzung bei Microsoft und kehrt zur Anmeldeseite zurück
  // (postLogoutRedirectUri in auth.ts). Ohne Entra (Dev-Modus) gibt es nichts abzumelden.
  // Fehler nicht verschlucken (z. B. interaction_in_progress): sonst tut der Knopf nichts,
  // und man hält sich für abgemeldet — siehe `anmelden` in AuthenticatedApp.
  function abmelden() {
    setMenue(null);
    if (!msalInstance) return;
    // Der nächste Benutzer am selben Browser soll nicht im Mandanten des Vorgängers starten.
    try {
      window.localStorage.removeItem(MANDANT_KEY);
    } catch {
      /* Ohne Speicher gibt es nichts zu entfernen. */
    }
    const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
    msalInstance.logoutRedirect(account ? { account } : undefined).catch((fehler: unknown) => {
      console.error('Abmelden fehlgeschlagen', fehler);
      toast(
        'Abmelden fehlgeschlagen. Bitte die Seite neu laden und noch einmal versuchen.',
        'error',
      );
    });
  }

  const offenRef = useRef(openModule);
  offenRef.current = openModule;

  const services: ShellServices = {
    bootstrap,
    activeTenant: tenant,
    // `/projektzeiten/heute` öffnet ein anderes Modul an einer Stelle; ein Pfad
    // ohne führenden Modul-Präfix ist eine Seite im gerade offenen Modul.
    navigate: (path) => {
      const ohne = path.replace(/^\//, '');
      const ziel = bootstrap.modules.find(
        (m) => `/${ohne}` === m.route || `/${ohne}`.startsWith(`${m.route}/`),
      );
      if (ziel && !nutzbar(ziel)) {
        toast(`${ziel.title} ist inaktiv und kann nicht geöffnet werden.`);
      } else if (ziel) {
        const rest = `/${ohne}`.slice(ziel.route.length).replace(/^\//, '');
        modulOeffnen(ziel, rest || undefined);
      } else if (offenRef.current) {
        seiteOeffnen(ohne);
      }
    },
    toast,
    confirm: rueckfragen.fragen,
    theme: () => themeRef.current,
    onThemeChange: (cb) => {
      themeListener.current.add(cb);
      return () => themeListener.current.delete(cb);
    },
    setSidebar: (id, sidebar) => {
      setSidebars((vorher) => ({ ...vorher, [id]: sidebar }));
      // Das offene Modul hat seine Seite selbst gemeldet: ab jetzt gilt `active`.
      if (id === offenRef.current?.id) setZielVorrang(false);
    },
    setBadgeCount: (id, count) => setBadges((vorher) => ({ ...vorher, [id]: count })),
  };

  // Mandantenwechsel verwirft die Seiten-Stände der Module. Nicht beim ersten Lauf:
  // sonst ginge ein Deep-Link auf `/<route>/@modul/<info>` verloren.
  const vorigerTenant = useRef(tenant.id);
  useEffect(() => {
    if (vorigerTenant.current === tenant.id) return;
    vorigerTenant.current = tenant.id;
    setSidebars({});
    setBadges({});
    setInfoSeite(null);
  }, [tenant.id]);

  const standards = useModulStandards(openModule, tenant.slug);

  async function simulationBeenden() {
    try {
      await apiFetch('verwaltung/simulation/beenden', { method: 'POST' }, tenant.slug);
    } finally {
      // Die Simulation ändert die gesamte Sicht — nach dem Beenden ist ein
      // Neuladen der ehrlichste Weg zurück zur eigenen.
      window.location.reload();
    }
  }

  function mandantWaehlen(slug: string) {
    setMenue(null);
    if (slug === tenant.slug) return;
    mandantWechseln(slug);
    startseite();
  }

  const modulSidebar = openModule ? sidebars[openModule.id] : undefined;

  // Adresse fortschreiben: Modul und aktive Seite (vom Modul gemeldet, sonst das letzte
  // Sprungziel). Ein Wechsel ist ein neuer Eintrag im Verlauf, damit „Zurück" funktioniert;
  // die allererste Angleichung nach dem Laden ersetzt nur (der Deep-Link wird zur Seite).
  const adresse = adresseFuer(
    openModule,
    infoSeite
      ? `${INFO_PRAEFIX}${infoSeite}`
      : zielVorrang
        ? ziel?.pfad
        : (modulSidebar?.active ?? ziel?.pfad),
  );
  const ersteAdresse = useRef(true);
  // Zuletzt geschriebene (oder bestätigte) Adresse — erkennt reine Hash-Wechsel.
  const geschrieben = useRef<string | null>(null);
  useEffect(() => {
    const schritt = adressSchritt({
      adresse,
      pfadname: window.location.pathname,
      ersterLauf: ersteAdresse.current,
      erwartet: erwartet.current,
      modulRoute: openModule?.route ?? null,
    });
    ersteAdresse.current = false;
    if (schritt === 'warten') return;
    erwartet.current = null;
    geschrieben.current = adresse;
    if (schritt === 'ersetzen') window.history.replaceState(null, '', adresse);
    else if (schritt === 'neu') window.history.pushState(null, '', adresse);
  }, [adresse, openModule, verlaufNr]);

  // Zurück und Vor im Browser: dieselbe Auswertung wie beim Laden.
  useEffect(() => {
    const zurueck = () => {
      const { pathname } = window.location;
      // Nur der Hash hat sich geändert (Anker im Modul) — kein Seitenwechsel der Shell.
      if (pathname === geschrieben.current) return;
      const s = startAusAdresse(bootstrap.modules);
      // Ein nicht (mehr) freigegebenes Modul im Verlauf landet auf der Startseite.
      if (!s.modul && pathname !== '/') window.history.replaceState(null, '', '/');
      erwartet.current = adresseFuer(s.modul, s.info ? `${INFO_PRAEFIX}${s.info}` : s.ziel?.pfad);
      if (s.modul && s.modul.id !== offenRef.current?.id) sidebarVerwerfen(s.modul.id);
      setOpenModule(s.modul);
      setInfoSeite(s.info);
      setZiel(s.ziel ? { ...s.ziel, nr: ++sprungNr.current } : undefined);
      setZielVorrang(true);
      setVerlaufNr((n) => n + 1);
    };
    window.addEventListener('popstate', zurueck);
    return () => window.removeEventListener('popstate', zurueck);
  }, [bootstrap.modules]);

  // Seiten des Moduls, darunter die Shell-eigene Gruppe „Modul" (ADR-008).
  const sidebar: ModuleSidebar | undefined = openModule
    ? {
        groups: [
          ...(modulSidebar?.groups.length
            ? modulSidebar.groups
            : [{ items: [{ path: '', label: 'Anwendung', icon: 'layout-dashboard' }] }]),
          infoGruppe(openModule, services),
        ],
        active: infoSeite ? `${INFO_PRAEFIX}${infoSeite}` : (modulSidebar?.active ?? ''),
      }
    : undefined;
  const aktiveSeite = infoSeite
    ? { label: infoTitel(infoSeite) }
    : modulSidebar?.groups.flatMap((g) => g.items).find((i) => i.path === modulSidebar.active);
  const phase: string | undefined = standards.data?.metadata?.status ?? openModule?.status;

  // Die Befehle der Palette: Module, ihre Seiten und Mandanten. Was jemand nicht
  // darf, steht gar nicht in `bootstrap` — und damit auch nicht hier.
  const befehle: Befehl[] = useMemo(
    () => [
      ...nutzbare.map((m) => ({
        id: `modul:${m.id}`,
        titel: m.title,
        bereich: 'Module',
        wo: m.category,
        icon: modulIcon(m),
        iconKnoten: m.iconKnoten,
        ausfuehren: () => modulOeffnen(m),
      })),
      ...Object.entries(sidebars).flatMap(([id, s]) => {
        const m = nutzbare.find((x) => x.id === id);
        if (!m) return [];
        return s.groups
          .flatMap((g) => g.items)
          .map((i) => ({
            id: `seite:${id}:${i.path}`,
            titel: i.label,
            bereich: 'Seiten',
            wo: m.title,
            icon: i.icon ?? modulIcon(m),
            iconKnoten: i.icon ? undefined : m.iconKnoten,
            ausfuehren: () => modulOeffnen(m, i.path),
          }));
      }),
      { id: 'home', titel: 'Startseite', bereich: 'Module', icon: 'house', ausfuehren: startseite },
      ...bootstrap.tenants.map((t) => ({
        id: `mandant:${t.id}`,
        titel: `Wechseln zu ${t.name}`,
        bereich: 'Mandant',
        icon: 'building-2',
        ausfuehren: () => mandantWaehlen(t.slug),
      })),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [nutzbare, bootstrap.tenants, sidebars, tenant.slug],
  );

  const logo = logoFuer(tenant, theme);

  return (
    <div className="shell">
      {bootstrap.simulation && (
        <SimulationsLeiste simulation={bootstrap.simulation} beenden={simulationBeenden} />
      )}

      <div className="one-app" data-sidebar={eingeklappt ? 'collapsed' : undefined}>
        {/* Rail: die Plattform-Ebene — Mandant, Module, Profil. */}
        <nav className="one-rail" aria-label="Plattform">
          <button
            type="button"
            className="one-rail-mark"
            style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer' }}
            onClick={startseite}
            aria-label="ONE – Startseite"
          >
            ONE<span className="dot">.</span>
          </button>
          <button
            type="button"
            className="one-rail-tenant"
            aria-label={`Mandant: ${tenant.name} – wechseln`}
            title={tenant.name}
            aria-haspopup="listbox"
            aria-expanded={menue === 'mandant'}
            disabled={bootstrap.tenants.length < 2}
            onClick={(e) => {
              e.stopPropagation();
              setMenue((m) => (m === 'mandant' ? null : 'mandant'));
            }}
          >
            {logo ? (
              <img src={logo} alt="" />
            ) : (
              <span className="initial">{tenant.name.charAt(0)}</span>
            )}
          </button>
          <div className="one-rail-sep" />
          <button
            type="button"
            className="one-rail-item"
            data-active={openModule === null ? 'true' : undefined}
            aria-current={openModule === null ? 'page' : undefined}
            aria-label="Startseite"
            title="Startseite"
            onClick={startseite}
          >
            <Icon name="house" size="rail" />
          </button>
          {bootstrap.modules.map((m) => (
            <button
              key={m.id}
              type="button"
              className="one-rail-item"
              data-active={openModule?.id === m.id ? 'true' : undefined}
              data-inaktiv={nutzbar(m) ? undefined : 'true'}
              aria-disabled={nutzbar(m) ? undefined : true}
              aria-current={openModule?.id === m.id ? 'page' : undefined}
              aria-label={badges[m.id] ? `${mitStatus(m)}, Handlungsbedarf` : mitStatus(m)}
              title={mitStatus(m)}
              onClick={() => nutzbar(m) && modulOeffnen(m)}
            >
              <Icon name={modulIcon(m)} knoten={m.iconKnoten} size="rail" />
              {(badges[m.id] ?? 0) > 0 && <span className="one-badge-dot" aria-hidden />}
            </button>
          ))}
          <div className="one-rail-bottom">
            <button
              type="button"
              className="one-rail-item"
              aria-label="Suchen (Strg+K)"
              title="Suchen (Strg+K)"
              onClick={befehlspaletteOeffnen}
            >
              <Icon name="search" size="rail" />
            </button>
            <button
              type="button"
              className="one-rail-item"
              aria-label={theme === 'dark' ? 'Helles Theme' : 'Dunkles Theme'}
              title={theme === 'dark' ? 'Helles Theme' : 'Dunkles Theme'}
              onClick={() => waehlen(theme === 'dark' ? 'light' : 'dark')}
            >
              <Icon name={theme === 'dark' ? 'sun' : 'moon'} size="rail" />
            </button>
            <button
              type="button"
              className="one-rail-item"
              aria-label={`Profil: ${bootstrap.user.displayName}`}
              aria-expanded={menue === 'profil'}
              onClick={(e) => {
                e.stopPropagation();
                setMenue((m) => (m === 'profil' ? null : 'profil'));
              }}
            >
              <Avatar
                initials={initialen(bootstrap.user.displayName)}
                label={bootstrap.user.displayName}
                size="sm"
              />
            </button>
          </div>
        </nav>

        {menue === 'mandant' && (
          <div className="shell-popover" data-pos="oben" onClick={(e) => e.stopPropagation()}>
            <MandantenMenue
              tenants={bootstrap.tenants}
              aktiv={tenant}
              theme={theme}
              wechseln={mandantWaehlen}
            />
          </div>
        )}
        {menue === 'profil' && (
          <div className="shell-popover" data-pos="unten" onClick={(e) => e.stopPropagation()}>
            <div className="one-menu shell-profil">
              <div>
                <strong>{bootstrap.user.displayName}</strong>
                <div className="muted">{bootstrap.user.email}</div>
              </div>
              <div className="one-help">
                {bootstrap.memberships.length === 1
                  ? '1 Mitgliedschaft'
                  : `${bootstrap.memberships.length} Mitgliedschaften`}{' '}
                ·{' '}
                {bootstrap.permissions.includes('*')
                  ? 'DEV'
                  : `${bootstrap.permissions.length} Rechte`}{' '}
                · Anmeldung über Microsoft
              </div>
              <span className="one-overline">Darstellung</span>
              <Segmented<ThemePreference>
                label="Darstellung"
                value={wahl}
                onChange={waehlen}
                options={[
                  { id: 'system', label: 'System' },
                  { id: 'light', label: 'Hell' },
                  { id: 'dark', label: 'Dunkel' },
                ]}
              />
              <div className="one-help">
                Rechte ändert die Verwaltung, nicht dieses Menü — und jede Änderung steht im
                Audit-Log.
              </div>
              {msalInstance && (
                <Button variant="secondary" size="sm" icon="log-out" onClick={abmelden}>
                  Abmelden
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Sidebar: gehört dem aktiven Modul (Seiten als Daten, Contract 0.4). */}
        <aside className="one-sidebar" aria-hidden={eingeklappt || undefined}>
          {openModule ? (
            <>
              <div className="one-sidebar-head">
                <Icon name={modulIcon(openModule)} knoten={openModule.iconKnoten} />
                <span className="one-sidebar-title">{openModule.title}</span>
                <Phase status={phase} />
              </div>
              <nav className="one-sidebar-body" aria-label={openModule.title}>
                {sidebar && sidebar.groups.length > 0 ? (
                  sidebar.groups.map((g, gi) => (
                    <div key={g.title ?? gi} className="one-nav-group">
                      {g.title && <div className="one-nav-head">{g.title}</div>}
                      {g.items.map((item) => {
                        const aktiv = item.path === sidebar.active;
                        const zahl = item.attention ?? item.count;
                        return (
                          <button
                            key={item.path}
                            type="button"
                            className="one-nav-item"
                            data-active={aktiv ? 'true' : undefined}
                            aria-current={aktiv ? 'page' : undefined}
                            onClick={() => {
                              seiteOeffnen(item.path);
                              if (istSchmal()) setEingeklappt(true);
                            }}
                          >
                            <Icon name={item.icon ?? 'circle'} />
                            <span className="one-nav-label">{item.label}</span>
                            {zahl !== undefined && zahl > 0 && (
                              <span
                                className="one-nav-count"
                                data-tone={item.attention !== undefined ? 'danger' : undefined}
                                aria-label={item.countLabel}
                              >
                                {zahl}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  ))
                ) : (
                  <div className="one-nav-group">
                    <button type="button" className="one-nav-item" data-active="true">
                      <Icon name="layout-dashboard" />
                      <span className="one-nav-label">Übersicht</span>
                    </button>
                  </div>
                )}
              </nav>
            </>
          ) : (
            <>
              <div className="one-sidebar-head">
                <Icon name="house" />
                <span className="one-sidebar-title">Start</span>
              </div>
              <nav className="one-sidebar-body" aria-label="Module">
                <div className="one-nav-group">
                  <button
                    type="button"
                    className="one-nav-item"
                    data-active="true"
                    aria-current="page"
                    onClick={startseite}
                  >
                    <Icon name="house" />
                    <span className="one-nav-label">Startseite</span>
                  </button>
                </div>
                {/* Gruppiert wie die Kacheln — eine Navigation, die anders sortiert
                    als die Startseite, zwingt zum zweimal Suchen. */}
                {nachGruppen(bootstrap.modules).map(([gruppe, module]) => (
                  <div key={gruppe} className="one-nav-group">
                    <div className="one-nav-head">{gruppe}</div>
                    {module.map((m) => (
                      <button
                        key={m.id}
                        type="button"
                        className="one-nav-item"
                        data-inaktiv={nutzbar(m) ? undefined : 'true'}
                        aria-disabled={nutzbar(m) ? undefined : true}
                        title={mitStatus(m)}
                        onClick={() => nutzbar(m) && modulOeffnen(m)}
                      >
                        <Icon name={modulIcon(m)} knoten={m.iconKnoten} />
                        <span className="one-nav-label">{m.title}</span>
                        <Phase status={m.status} />
                        {(badges[m.id] ?? 0) > 0 && (
                          <span className="one-nav-count" data-tone="danger">
                            {badges[m.id]}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                ))}
              </nav>
            </>
          )}
          <TeamHeute mandant={tenant.slug} />
        </aside>

        <div className="one-main">
          <header className="one-topbar">
            <IconButton
              icon="panel-left"
              label={eingeklappt ? 'Sidebar einblenden' : 'Sidebar ausblenden'}
              size="sm"
              onClick={() => {
                // Speichern außerhalb des Updaters: der bleibt frei von Nebenwirkungen.
                if (!istSchmal()) speichern(SIDEBAR_KEY, eingeklappt ? 'auf' : 'zu');
                setEingeklappt(!eingeklappt);
              }}
            />
            <nav className="one-crumbs" aria-label="Brotkrume">
              <span className="shell-crumb-anker">
                <button
                  type="button"
                  className="one-tenant-chip"
                  aria-haspopup="listbox"
                  aria-expanded={menue === 'mandant-crumb'}
                  disabled={bootstrap.tenants.length < 2}
                  onClick={(e) => {
                    e.stopPropagation();
                    setMenue((m) => (m === 'mandant-crumb' ? null : 'mandant-crumb'));
                  }}
                >
                  <span className="sw" />
                  {tenant.name}
                </button>
                {menue === 'mandant-crumb' && (
                  <div className="shell-popover-crumb" onClick={(e) => e.stopPropagation()}>
                    <MandantenMenue
                      tenants={bootstrap.tenants}
                      aktiv={tenant}
                      theme={theme}
                      wechseln={mandantWaehlen}
                    />
                  </div>
                )}
              </span>
              <span className="sep" aria-hidden>
                ›
              </span>
              {openModule ? (
                <>
                  {aktiveSeite ? (
                    <button
                      type="button"
                      className="crumb"
                      onClick={() => {
                        const erste = modulSidebar?.groups[0]?.items[0];
                        seiteOeffnen(erste?.path ?? '');
                      }}
                    >
                      {openModule.title}
                    </button>
                  ) : (
                    <span className="current" aria-current="page">
                      {openModule.title}
                    </span>
                  )}
                  {aktiveSeite && (
                    <>
                      <span className="sep" aria-hidden>
                        ›
                      </span>
                      <span className="current" aria-current="page">
                        {aktiveSeite.label}
                      </span>
                    </>
                  )}
                </>
              ) : (
                <span className="current" aria-current="page">
                  Startseite
                </span>
              )}
            </nav>
            <span className="one-topbar-spacer" />
            {/* Ein Feld statt eines Tastenkürzel-Hinweises: `Strg+K` findet nur,
                wer es kennt. */}
            <button
              type="button"
              className="one-search"
              aria-label="Suchen oder springen"
              onClick={befehlspaletteOeffnen}
            >
              <Icon name="search" />
              <span>Suchen oder springen …</span>
              <Kbd>Strg K</Kbd>
            </button>
          </header>

          <main className="one-content">
            {openModule ? (
              <>
                {/* Verborgen statt entfernt: beim Lesen der Hilfe bleiben Eingaben erhalten. */}
                <div hidden={infoSeite !== null} className="one-stack" data-gap="6">
                  <ModuleHost entry={openModule} services={services} ziel={ziel} />
                </div>
                {infoSeite && (
                  <ModulInfo
                    seite={infoSeite}
                    entry={openModule}
                    services={services}
                    standards={standards}
                  />
                )}
              </>
            ) : (
              <Startseite
                bootstrap={bootstrap}
                kennzahlen={kennzahlen}
                hinweise={hinweise}
                oeffnen={modulOeffnen}
              />
            )}
          </main>
        </div>
      </div>

      <Befehlspalette befehle={befehle} />
      <Toasts items={toasts} />
      <Rueckfragedialog rueckfragen={rueckfragen} />
    </div>
  );
}

function Mitte({ children }: { children: React.ReactNode }) {
  return <div className="shell-mitte">{children}</div>;
}

/**
 * Das Microsoft-Zeichen auf der Anmelde-Schaltfläche: vier Quadrate in Microsofts Markenfarben,
 * wie Microsoft es für „Mit Microsoft anmelden“ vorgibt. Es ist ein Herkunftszeichen des
 * Anmeldedienstes, kein Icon — die einzige farbige Marke in ONE (Brand Book, „Iconografie“).
 */
function MicrosoftZeichen() {
  return (
    <svg className="shell-ms-zeichen" viewBox="0 0 21 21" aria-hidden="true" focusable="false">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}

/**
 * Weckt die API beim Öffnen der Shell auf (DEV skaliert außerhalb der Geschäftszeit
 * auf 0, Kaltstart ca. 30 s) und hält sie während der Nutzung wach. `/core/health`
 * ist öffentlich — die Abfrage braucht kein Token und kein Bootstrap.
 */
function useApiAufwaermen(): ApiStatus {
  const [status, setStatus] = useState<ApiStatus>('unbekannt');

  useEffect(() => {
    const aufwaermer = erzeugeAufwaermer({
      bei: setStatus,
      pruefen: async () => {
        const abbruch = new AbortController();
        const timer = setTimeout(() => abbruch.abort(), 10_000);
        try {
          const r = await fetch(`${API_BASE}/core/health`, {
            signal: abbruch.signal,
            cache: 'no-store',
          });
          return r.ok;
        } catch {
          return false;
        } finally {
          clearTimeout(timer);
        }
      },
    });
    const sichtbarkeit = () => aufwaermer.sichtbar(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', sichtbarkeit);
    void aufwaermer.aufwaermen();
    sichtbarkeit();
    return () => {
      document.removeEventListener('visibilitychange', sichtbarkeit);
      aufwaermer.stop();
    };
  }, []);

  return status;
}

function ApiHinweis({ status }: { status: ApiStatus }) {
  if (status === 'startet')
    return (
      <div className="shell-api-hinweis" role="status" aria-live="polite">
        <Notice tone="warn" title="API startet …">
          Der erste Aufruf kann ca. 30 Sekunden dauern.
        </Notice>
      </div>
    );
  if (status === 'bereit')
    return (
      <div className="shell-api-hinweis" role="status" aria-live="polite">
        <Notice tone="accent" title="API bereit" />
      </div>
    );
  if (status === 'fehler')
    return (
      <div className="shell-api-hinweis" role="status">
        <Notice tone="danger" title="Die API antwortet nicht.">
          Der Start hat länger als zwei Minuten gedauert. Bitte die Seite später neu laden.
        </Notice>
      </div>
    );
  return null;
}

function Loaded() {
  const apiStatus = useApiAufwaermen();
  return (
    <>
      <ApiHinweis status={apiStatus} />
      <LoadedInhalt />
    </>
  );
}

function LoadedInhalt() {
  const [mandant, setMandant] = useState<string | undefined>(
    () => gespeichert(MANDANT_KEY) ?? undefined,
  );
  const { data, geladenFuer, error, startet } = useBootstrap(mandant);
  const mandantMerken = useCallback((slug: string) => {
    speichern(MANDANT_KEY, slug);
    setMandant(slug);
  }, []);
  // Ist der gemerkte Mandant nicht (mehr) erlaubt, hat die API einen anderen gewählt. Wunsch
  // und Speicher daran angleichen — sonst bliebe eine spätere Wahl desselben Mandanten
  // wirkungslos, weil sich der Zustand nicht ändert und kein neuer Bootstrap startet.
  const aktiverSlug = data?.tenants.find((t) => t.id === data.activeTenantId)?.slug;
  useEffect(() => {
    if (!aktiverSlug || mandant === undefined || geladenFuer !== mandant) return;
    if (aktiverSlug === mandant) return;
    speichern(MANDANT_KEY, aktiverSlug);
    setMandant(aktiverSlug);
  }, [aktiverSlug, geladenFuer, mandant]);

  // Vor dem Bootstrap gilt das ONE-Set und die Systemeinstellung (kein Mandant).
  useEffect(() => {
    if (!data) {
      const wahl = (gespeichert(THEME_KEY) as ThemePreference | null) ?? 'system';
      applyTheme(document.documentElement, { theme: resolveTheme(wahl) });
    }
  }, [data]);

  if (error) {
    return (
      <Mitte>
        <Notice tone="danger" title="Die Plattform-API ist nicht erreichbar.">
          {error}
        </Notice>
      </Mitte>
    );
  }
  if (!data)
    return (
      <Mitte>
        <p className="muted">{startet ? 'Die Plattform startet …' : 'Lade Plattform …'}</p>
      </Mitte>
    );
  // Grant-only (Kap. 04 §1.1): ohne Freischaltung oder ohne einen einzigen
  // Mandanten gibt es nichts zu zeigen — und das sagt die Wartekarte.
  if (!data.user.zugelassen || data.tenants.length === 0)
    return (
      <Mitte>
        <Wartekarte user={data.user} />
      </Mitte>
    );
  return <Platform bootstrap={data} mandantWechseln={mandantMerken} />;
}

/** Mit Entra: Login-Gate vor der Plattform. */
function AuthenticatedApp() {
  const { instance } = useMsal();
  const [loginFehler, setLoginFehler] = useState<string | null>(null);

  useEffect(() => {
    const wahl = (gespeichert(THEME_KEY) as ThemePreference | null) ?? 'system';
    applyTheme(document.documentElement, { theme: resolveTheme(wahl) });
  }, []);

  // Ein verschluckter Fehler ist hier besonders teuer: der Knopf tut dann einfach
  // nichts, und niemand kann sehen, warum. Genau so ist eine Fehlkonfiguration des
  // MSAL-Caches am 27.08. bis in die Live-Umgebung gekommen.
  const anmelden = () => {
    setLoginFehler(null);
    instance.loginRedirect(loginRequest).catch((fehler: unknown) => {
      const text = fehler instanceof Error ? fehler.message : String(fehler);
      console.error('Anmeldung konnte nicht gestartet werden', fehler);
      setLoginFehler(text);
    });
  };

  return (
    <>
      <AuthenticatedTemplate>
        <Loaded />
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        {/* Anmeldebildschirm nach den Prototypen vom 31.10.2026 (Entscheidung Enrico): „Das Licht“
            hinter der Karte, Schaltfläche mit Microsoft-Zeichen, Hinweissatz ohne „VERCH-Gruppe“.
            Das Licht ist reines CSS (styles.css, .shell-licht); Karte und Knopf bleiben still. */}
        <div className="shell-mitte shell-anmeldung">
          <div className="shell-licht" aria-hidden="true">
            <span className="shell-licht-a" />
            <span className="shell-licht-b" />
            <span className="shell-licht-c" />
          </div>
          <Card>
            <span className="shell-wortmarke">
              ONE<span className="dot">.</span>
            </span>
            <h1 className="one-page-title">Willkommen bei ONE.</h1>
            <p className="one-prose">Anmeldung mit deinem Microsoft-Konto.</p>
            <div className="one-actions">
              <Button size="lg" variant="secondary" className="shell-anmelden" onClick={anmelden}>
                <MicrosoftZeichen />
                Mit Microsoft anmelden
              </Button>
            </div>
            {loginFehler && (
              <Notice tone="danger" title="Die Anmeldung ließ sich nicht starten.">
                {loginFehler}
              </Notice>
            )}
          </Card>
        </div>
      </UnauthenticatedTemplate>
    </>
  );
}

/** Ohne Entra-Konfiguration: direkt die Plattform, klar als Dev-Modus markiert. */
function DevApp() {
  return (
    <div className="shell">
      <div className="shell-leiste">
        DEV-MODUS · Entra nicht konfiguriert — siehe docs/setup/entra-setup.md und
        apps/shell/.env.example
      </div>
      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Loaded />
      </div>
    </div>
  );
}

export function App() {
  return authConfigured ? <AuthenticatedApp /> : <DevApp />;
}
